/**
 * Track text selection within a container element.
 * Handles both regular DOM selections (markdown/code) and
 * iframe selections (HTML frames) via postMessage bridge.
 */
import { useState, useEffect, useCallback, useRef } from "react";

interface SelectionState {
  text: string;
  rect: DOMRect | null;
}

interface UseTextSelectionOptions {
  /** Ref to the container element to watch for selections */
  containerRef: React.RefObject<HTMLElement | null>;
  /** If true, also listen for postMessage from iframes */
  iframeBridge?: boolean;
}

interface UseTextSelectionReturn {
  /** Currently selected text (empty string if none) */
  selectedText: string;
  /** Bounding rect of the selection relative to the viewport */
  selectionRect: DOMRect | null;
  /** Clear the current selection state */
  clearSelection: () => void;
}

export function useTextSelection({
  containerRef,
  iframeBridge = false,
}: UseTextSelectionOptions): UseTextSelectionReturn {
  const [selection, setSelection] = useState<SelectionState>({
    text: "",
    rect: null,
  });
  const selectionRef = useRef(selection);
  selectionRef.current = selection;

  const clearSelection = useCallback(() => {
    setSelection({ text: "", rect: null });
  }, []);

  /* Regular DOM selection (markdown/code frames).
   * Listens on document so it works even when containerRef.current is null
   * at effect setup time (ref gets populated after first render). */
  useEffect(() => {
    let scheduledFrame = 0;
    function readSelection() {
      const container = containerRef.current;
      const sel = window.getSelection();
      if (!container || !sel || sel.isCollapsed || !sel.rangeCount) {
        if (!document.activeElement?.closest?.("[data-selection-toolbar]")) {
          setSelection({ text: "", rect: null });
        }
        return;
      }
      const text = sel.toString().trim();
      if (!text) {
        setSelection({ text: "", rect: null });
        return;
      }
      const range = sel.getRangeAt(0);
      if (!container.contains(range.commonAncestorContainer)) {
        setSelection({ text: "", rect: null });
        return;
      }
      setSelection({ text, rect: range.getBoundingClientRect() });
    }

    function scheduleSelectionRead() {
      cancelAnimationFrame(scheduledFrame);
      scheduledFrame = requestAnimationFrame(readSelection);
    }

    function handleMouseDown(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (target.closest("[data-selection-toolbar]")) return;
      if (selectionRef.current.text) {
        setSelection({ text: "", rect: null });
      }
    }

    document.addEventListener("mouseup", scheduleSelectionRead);
    document.addEventListener("selectionchange", scheduleSelectionRead);
    document.addEventListener("mousedown", handleMouseDown);

    return () => {
      cancelAnimationFrame(scheduledFrame);
      document.removeEventListener("mouseup", scheduleSelectionRead);
      document.removeEventListener("selectionchange", scheduleSelectionRead);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [containerRef]);

  /* iframe postMessage bridge (HTML frames) */
  useEffect(() => {
    if (!iframeBridge) return;

    function handleMessage(e: MessageEvent) {
      if (e.data?.type !== "denker-text-selection") return;
      const container = containerRef.current;
      if (!container) return;
      const iframe = container.querySelector("iframe");
      if (!iframe) return;
      if (e.source !== iframe.contentWindow) return;
      const { text, rect: iframeRect } = e.data;

      if (!text?.trim()) {
        setSelection({ text: "", rect: null });
        return;
      }

      // Translate iframe-local coordinates to viewport coordinates
      const iframeBox = iframe.getBoundingClientRect();

      const viewportRect = new DOMRect(
        iframeBox.left + iframeRect.x,
        iframeBox.top + iframeRect.y,
        iframeRect.width,
        iframeRect.height,
      );

      setSelection({ text: text.trim(), rect: viewportRect });
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [iframeBridge, containerRef]);

  return {
    selectedText: selection.text,
    selectionRect: selection.rect,
    clearSelection,
  };
}
